import classNames from 'classnames';
import { ReactNode } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { Horizontal } from 'atoms/layout/flex';
import { HexColor } from 'style/types';

const Root = styled(Horizontal)`
  width: 100%;

  --segment-fill-color: var(--color);
  --glow-color: var(--color);

  &.default {
    --segments-gap: 1px;
    --segments-height: var(--unit);
  }

  &.oblique {
    --segments-oblique-angle: var(--half-unit);
    --segments-gap: 0px;
    --segments-height: var(--double-unit);

    &.split {
      --segments-gap: var(--half-unit);
      --segments-height: var(--intermediate-unit);
    }
  }
`;

const SegmentsWrapper = styled(Horizontal)`
  gap: var(--segments-gap);
  flex: 1;

  & > * {
    flex: 1;
  }

  .oblique & {
    position: relative;
    isolation: isolate;
    height: var(--segments-height);
  }
`;

const Segment = styled.div`
  height: var(--unit);

  &.hide {
    display: none;
  }

  .default & {
    position: relative;
    overflow: hidden;
    isolation: isolate;

    border-radius: 2px;
    background-color: var(--c-nd-150);

    .iridescent & {
      --segment-fill-color: linear-gradient(
        84deg,
        #f8d3da 0%,
        #b3a9f4 28.32%,
        #fbe9fb 54.01%,
        #4f94fd 100%
      );
      --glow-color: #4f94fd;
    }

    &.full {
      inset: 0;
      background: var(--segment-fill-color);
      box-shadow: 0px 0px 10px 0px rgba(255, 255, 255, 0.65) inset;
    }

    .glow &.full {
      filter: drop-shadow(0px 1px 7px var(--glow-color));
    }

    &.negativePartial {
      --partial-opacity: 0.25;
      --partial-background: linear-gradient(
          to right,
          var(--c-nl-50) 30%,
          var(--c-nl-500) 100%
        ),
        var(--segment-fill-color);
    }

    &.partial {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: var(--default-segment-width);
        background: var(--partial-background, var(--segment-fill-color));

        opacity: var(--partial-opacity, 0.5);
      }
    }
  }

  .oblique.split & {
    left: var(--oblique-segment-offset);
  }

  .oblique & {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: calc(100% - var(--oblique-segment-width));
    height: 100%;
    z-index: var(--oblique-segment-z-index);

    clip-path: polygon(
      var(--segments-oblique-angle) 0%,
      100% 0%,
      calc(100% - var(--segments-oblique-angle)) 100%,
      0% 100%
    );

    &.full {
      background: var(--segment-fill-color);
    }

    &.empty {
      background: linear-gradient(
        270deg,
        var(--c-nd-50-opaque) 1.98%,
        var(--c-nd-150-opaque) 85.3%
      );
    }

    &.negativePartial {
      --partial-opacity: 0.25;
      --partial-background: linear-gradient(
          to right,
          var(--c-nl-50) 30%,
          var(--c-nl-500) 100%
        ),
        var(--segment-fill-color);
    }

    &.partial:not(.empty) {
      background: var(
        --partial-background,
        linear-gradient(
          270deg,
          var(--c-nd-50-opaque) 1.98%,
          var(--c-nd-150-opaque) 85.3%
        )
      );
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: var(--default-segment-width);
      background: var(--partial-background, var(--segment-fill-color));

      clip-path: polygon(
        var(--segments-oblique-angle) 0%,
        100% 0%,
        calc(100% - var(--segments-oblique-angle)) 100%,
        0% 100%
      );
      opacity: var(--partial-opacity, 0.5);
    }
  }
`;

type SegmentData = {
  isFull: boolean;
  isPartial: boolean;
  isEmpty: boolean;
  isNegative: boolean;
  defaultSegmentWidth?: string;
  obliqueSegmentWidth: string;
  obliqueSegmentOffset: string;
  obliqueSegmentZIndex: number;
  hide?: boolean;
};
type Props = {
  thresholds: { score: number }[];
  currentScore: number;
  previewScore?: number;
  color: HexColor | 'iridescent';
  label?: ReactNode;
  glow?: boolean;
  variant?: 'oblique' | 'default';
  split?: boolean;
};

export const SplitProgressBar = ({
  thresholds,
  currentScore: referenceScore,
  previewScore: optionalScore,
  color,
  label,
  glow,
  variant = 'default',
  split = true,
}: Props) => {
  if (!thresholds.length) {
    return null;
  }

  const previewScore = Math.max(
    optionalScore ?? referenceScore,
    referenceScore
  );
  const validScore = Math.min(optionalScore ?? referenceScore, referenceScore);

  const progressBarScoreMax = thresholds[thresholds.length - 1].score;
  const progressBarScoreUnit = 1 / progressBarScoreMax;

  // Compute all segments
  const segments: SegmentData[] = thresholds.map((threshold, index) => {
    const fullBar = validScore >= threshold.score;
    const previousBonusThresholdValue =
      (index > 0 && thresholds.at(index - 1)?.score) || 0;
    const partialBar = !fullBar && previewScore >= previousBonusThresholdValue;
    const defaultSegmentWidth = partialBar
      ? Math.min(
          1,
          (previewScore - previousBonusThresholdValue) /
            (threshold.score - previousBonusThresholdValue)
        )
      : undefined;

    const fullLayer = 2;
    const partialLayer = 1;
    const emptyLayer = 0;

    const isPartial = !!partialBar;
    const isFull = fullBar && !isPartial;
    const isEmpty = !fullBar && !defaultSegmentWidth;

    // in `oblique` variant, we want to display the full segment first, then the partial one, then the empty one
    let obliqueSegmentZIndex = thresholds.length;
    if (isFull) {
      obliqueSegmentZIndex = obliqueSegmentZIndex * fullLayer + index;
    } else if (isEmpty) {
      obliqueSegmentZIndex = obliqueSegmentZIndex * emptyLayer + index;
    } else if (isPartial) {
      obliqueSegmentZIndex = obliqueSegmentZIndex * partialLayer + index;
    }

    return {
      isFull,
      isPartial,
      isEmpty,
      isNegative: (optionalScore ?? referenceScore) < referenceScore,
      defaultSegmentWidth: `calc(${defaultSegmentWidth} * 100%)`,
      obliqueSegmentZIndex,
      obliqueSegmentOffset: `calc(${index ? 'var(--segments-gap)' : '0px'} + (${previousBonusThresholdValue * progressBarScoreUnit} * 100%))`,
      obliqueSegmentWidth: `calc(${threshold.score * progressBarScoreUnit} * 100%)`,
    };
  });

  const formattedSegments = segments.map((segment, segmentIndex) => {
    const previousSegment = segments[segmentIndex + 1] || segment;

    // in `oblique` variant, we only display the last full segment, the last partial one, and the last empty one
    if (
      !split &&
      ((previousSegment?.isPartial && !segment.isPartial) ||
        (previousSegment?.isFull && !segment.isFull) ||
        (previousSegment?.isEmpty && !segment.isEmpty) ||
        segmentIndex === segments.length - 1)
    ) {
      return {
        ...segment,
        hide: false,
      };
    }
    return { ...segment, hide: !split };
  });

  return (
    <Root
      style={{ '--color': color } as CSSProperties}
      className={classNames(variant, {
        split,
        glow,
        iridescent: color === 'iridescent',
      })}
      role="progressbar"
    >
      <SegmentsWrapper>
        {formattedSegments.map((segment, index) => (
          <Segment
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={classNames({
              full: segment.isFull,
              partial: segment.isPartial,
              empty: segment.isEmpty,
              negativePartial: segment.isNegative,
              hide: segment.hide,
            })}
            style={
              {
                '--default-segment-width': segment.defaultSegmentWidth,
                '--oblique-segment-width': segment.obliqueSegmentWidth,
                '--oblique-segment-offset': segment.obliqueSegmentOffset,
                '--oblique-segment-z-index': segment.obliqueSegmentZIndex,
              } as CSSProperties
            }
          />
        ))}
      </SegmentsWrapper>
      {label}
    </Root>
  );
};
